/**
 * Flow Builder Graph Validation Utility
 *
 * Validates a complete flow (nodes, edges, variables) and returns
 * errors (block save/export) and warnings (informational only).
 *
 * This is for the React Flow–based Flow Builder (nodes/edges),
 * not the WhatsApp Meta Flows JSON validator.
 */

import { getUnknownVariableNames } from "./flowVariableValidation";
import { AI_HELP_KB_MAX_LENGTH } from "./flowAiHelpUtils";

// ── Helpers ──

const URL_PATTERN = /^https?:\/\/.+/i;
const PHONE_PATTERN = /^\+?\d{7,15}$/;
const VALID_DELAY_UNITS = ["seconds", "minutes", "hours", "days"];
const MAX_BODY_LENGTH = 1024;
const MAX_HEADER_TEXT_LENGTH = 60;
const MAX_FOOTER_TEXT_LENGTH = 60;
const MAX_BUTTON_TEXT_LENGTH = 20;
const MAX_BUTTONS_TOTAL = 6;
const MAX_QUICK_REPLY = 3;
const MAX_URL_BUTTONS = 2;
const MAX_PHONE_BUTTONS = 1;
const MAX_SET_VARIABLE_ITEMS = 20;
const VALID_SET_VARIABLE_NAME = /^[a-z_][a-z0-9_]*$/;
const ALLOWED_MEDIA_TYPES = ["image", "video", "document", "audio"];

/**
 * Check unknown variables in a text field and push errors if found.
 * @param {string}  text           - The text to scan for {{variable}} placeholders
 * @param {Array}   flowVariables  - The centralized variable registry
 * @param {string}  nodeId         - Node id for the error entry
 * @param {string}  label          - Human-readable node label
 * @param {string}  fieldName      - e.g. "body", "header", "footer", "URL button"
 * @param {Array}   target         - errors or warnings array to push to
 */
function checkUnknownVars(text, flowVariables, nodeId, label, fieldName, target) {
  if (!text) return;
  const unknown = getUnknownVariableNames(text, flowVariables);
  if (unknown.length > 0) {
    target.push({
      nodeId,
      message: `"${label}": Unknown variable(s) in ${fieldName}: {{${unknown.join("}}, {{")}}}`,
    });
  }
}

/**
 * Validate the entire flow graph (nodes + edges) before saving.
 *
 * @param {Array} nodes  - ReactFlow nodes array (each has id, type, data)
 * @param {Array} edges  - ReactFlow edges array (each has source, target)
 * @param {Array} flowVariables - Centralized variable registry [{ name, ... }]
 * @returns {{ valid: boolean, errors: Array<{ nodeId?: string, message: string }>, warnings: Array<{ nodeId?: string, message: string }> }}
 */
export function validateFlowGraph(nodes = [], edges = [], flowVariables = []) {
  const errors = [];
  const warnings = [];

  const nodeIds = new Set(nodes.map((n) => n.id));

  // ── Global checks ──
  const triggerNodes = nodes.filter((n) => n.type === "trigger");
  if (triggerNodes.length === 0) {
    errors.push({ message: "Flow must have at least one Trigger node." });
  }
  if (triggerNodes.length > 1) {
    warnings.push({
      message: "Flow has more than one Trigger node. Only one trigger is recommended.",
    });
  }

  // Check for nodes with no incoming edges (except trigger and knowledgeBase)
  for (const node of nodes) {
    if (node.type === "trigger" || node.type === "knowledgeBase") continue;
    const hasIncoming = edges.some((e) => e.target === node.id);
    if (!hasIncoming) {
      warnings.push({
        nodeId: node.id,
        message: `"${node.data?.label || node.type}": Node has no incoming connection and may be unreachable.`,
      });
    }
  }

  // ── Per-node checks ──
  for (const node of nodes) {
    const d = node.data || {};
    const label = d.label || node.type || node.id;

    switch (node.type) {
      // ─── TRIGGER ───
      case "trigger": {
        const triggerType = d.triggerType || "keyword";
        if (triggerType === "keyword") {
          const anyKeyword = d.triggerOnAnyKeyword || false;
          const keywords = d.keywords || [];
          if (!anyKeyword && keywords.length === 0) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Keyword trigger has no keywords set. Add at least one keyword or enable "Trigger on any message".`,
            });
          }
        }

        if (triggerType === "api") {
          const apiVars = d.apiExpectedVariables || {};
          const allEntries = [
            ...(Array.isArray(apiVars.required) ? apiVars.required : []),
            ...(Array.isArray(apiVars.optional) ? apiVars.optional : []),
          ];
          const seen = new Set();
          for (const entry of allEntries) {
            const name = typeof entry === "string" ? entry.trim() : String(entry?.name || "").trim();
            if (!name) continue;
            if (!/^[a-z_][a-z0-9_]*$/.test(name)) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": Invalid API variable name "${name}". Use lowercase letters, numbers, and underscores.`,
              });
            }
            const key = name.toLowerCase();
            if (seen.has(key)) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": Duplicate API variable "${name}" in required/optional lists.`,
              });
            }
            seen.add(key);
          }
        }

        if (d.allowAiHelpDuringConversation === true) {
          const kb = (d.aiHelpKnowledgeBase || "").trim();
          if (!kb) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": AI help is enabled but knowledge base context is empty.`,
            });
          }
          if ((d.aiHelpKnowledgeBase || "").length > AI_HELP_KB_MAX_LENGTH) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": AI help knowledge base exceeds ${AI_HELP_KB_MAX_LENGTH} characters.`,
            });
          }
        } else if ((d.aiHelpKnowledgeBase || "").length > AI_HELP_KB_MAX_LENGTH) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": AI help knowledge base exceeds ${AI_HELP_KB_MAX_LENGTH} characters.`,
          });
        }
        break;
      }

      // ─── TEMPLATE (WhatsApp message) ───
      case "template": {
        const bodyText = d.body?.text || "";
        if (!bodyText.trim()) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Template body text is empty.`,
          });
        }
        if (bodyText.length > MAX_BODY_LENGTH) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Template body exceeds ${MAX_BODY_LENGTH} characters (${bodyText.length}).`,
          });
        }
        checkUnknownVars(bodyText, flowVariables, node.id, label, "body", errors);

        const headerFormat = d.header?.format || "NONE";
        if (headerFormat === "TEXT") {
          const headerText = d.header?.text || "";
          if (!headerText.trim()) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Header text is required when header format is TEXT.`,
            });
          }
          if (headerText.length > MAX_HEADER_TEXT_LENGTH) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Header text exceeds ${MAX_HEADER_TEXT_LENGTH} characters (${headerText.length}).`,
            });
          }
          checkUnknownVars(headerText, flowVariables, node.id, label, "header", errors);
        }
        if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat)) {
          const mediaUrl = d.header?.mediaUrl || "";
          if (!mediaUrl.trim()) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Please upload a ${headerFormat.toLowerCase()} for the header or provide a media URL.`,
            });
          }
          checkUnknownVars(mediaUrl, flowVariables, node.id, label, "header media URL", warnings);
        }

        const footerText = d.footer?.text || "";
        if (footerText.length > MAX_FOOTER_TEXT_LENGTH) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Footer text exceeds ${MAX_FOOTER_TEXT_LENGTH} characters (${footerText.length}).`,
          });
        }
        checkUnknownVars(footerText, flowVariables, node.id, label, "footer", warnings);

        const buttons = d.buttons || [];
        if (buttons.length > MAX_BUTTONS_TOTAL) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Too many buttons (${buttons.length}). Maximum allowed is ${MAX_BUTTONS_TOTAL}.`,
          });
        }
        const qrCount = buttons.filter((b) => b.type === "QUICK_REPLY").length;
        const urlCount = buttons.filter((b) => b.type === "URL").length;
        const phoneCount = buttons.filter((b) => b.type === "PHONE_NUMBER").length;
        if (qrCount > MAX_QUICK_REPLY) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Too many Quick Reply buttons (${qrCount}). Maximum is ${MAX_QUICK_REPLY}.`,
          });
        }
        if (urlCount > MAX_URL_BUTTONS) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Too many URL buttons (${urlCount}). Maximum is ${MAX_URL_BUTTONS}.`,
          });
        }
        if (phoneCount > MAX_PHONE_BUTTONS) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Too many Phone buttons (${phoneCount}). Maximum is ${MAX_PHONE_BUTTONS}.`,
          });
        }

        for (const btn of buttons) {
          const btnLabel = btn.text || btn.type;
          if (!(btn.text || "").trim()) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Button "${btnLabel}" has no label text.`,
            });
          }
          if ((btn.text || "").length > MAX_BUTTON_TEXT_LENGTH) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Button "${btnLabel}" label exceeds ${MAX_BUTTON_TEXT_LENGTH} characters.`,
            });
          }

          if (btn.type === "URL") {
            const btnUrl = btn.url || "";
            if (!btnUrl.trim()) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": URL button "${btnLabel}" has no URL.`,
              });
            } else if (!URL_PATTERN.test(btnUrl.replace(/\{\{\w+\}\}/g, "placeholder"))) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": URL button "${btnLabel}" has an invalid URL. Must start with http:// or https://.`,
              });
            }
            checkUnknownVars(btnUrl, flowVariables, node.id, label, `URL button "${btnLabel}"`, errors);
          }

          if (btn.type === "PHONE_NUMBER") {
            const phone = btn.phone_number || "";
            if (!phone.trim()) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": Phone button "${btnLabel}" has no phone number.`,
              });
            } else if (!PHONE_PATTERN.test(phone.replace(/[\s\-()]/g, ""))) {
              warnings.push({
                nodeId: node.id,
                message: `"${label}": Phone button "${btnLabel}" number "${phone}" may be invalid. Use international format e.g. +1234567890.`,
              });
            }
          }
        }

        break;
      }

      // ─── TEXT MESSAGE ───
      case "textMessage": {
        const msg = d.message || "";
        if (!msg.trim()) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Text message is empty.`,
          });
        }
        checkUnknownVars(msg, flowVariables, node.id, label, "message", errors);
        break;
      }

      // ─── SEND MEDIA ───
      case "sendMedia": {
        const mode = d.mode || "manual";
        if (!["manual", "variable"].includes(mode)) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Mode must be "manual" or "variable" (got "${mode}").`,
          });
          break;
        }

        if (mode === "manual") {
          const items = d.items || [];
          if (items.length === 0) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Add at least one media item.`,
            });
          }
          items.forEach((item, idx) => {
            const pos = `item #${idx + 1}`;
            if (!ALLOWED_MEDIA_TYPES.includes(item.type)) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": ${pos} has invalid type "${item.type}". Use one of: ${ALLOWED_MEDIA_TYPES.join(", ")}.`,
              });
            }
            const url = (item.mediaUrl || "").trim();
            if (!url) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": ${pos} is missing a media URL or upload.`,
              });
            } else {
              checkUnknownVars(url, flowVariables, node.id, label, `${pos} URL`, warnings);
            }
            if (item.caption) {
              checkUnknownVars(item.caption, flowVariables, node.id, label, `${pos} caption`, warnings);
            }
          });
        } else {
          // mode === "variable"
          const varName = (d.variableName || "").trim();
          if (!varName) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Choose a variable that holds the media list.`,
            });
          } else {
            const known = (flowVariables || []).some((v) => v.name === varName);
            if (!known) {
              warnings.push({
                nodeId: node.id,
                message: `"${label}": Variable "${varName}" is not declared. Make sure it's set earlier in the flow (e.g. by a Webhook).`,
              });
            }
          }
        }
        break;
      }

      // ─── SET VARIABLE ───
      case "setVariable": {
        const items = Array.isArray(d.items) ? d.items : [];
        if (items.length === 0) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Add at least one row to set.`,
          });
        }
        if (items.length > MAX_SET_VARIABLE_ITEMS) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Too many rows (${items.length}). Maximum allowed is ${MAX_SET_VARIABLE_ITEMS}.`,
          });
        }
        // Track duplicates within this node (last write wins, but warn).
        const seenTargets = new Set();
        items.forEach((item, idx) => {
          const pos = `row #${idx + 1}`;
          const target = (item.variableName || "").trim();
          if (!target) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": ${pos} is missing a target variable.`,
            });
          } else if (!VALID_SET_VARIABLE_NAME.test(target)) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": ${pos} target "${target}" is not a valid variable name (use lowercase letters, digits, and underscores; must start with a letter or underscore).`,
            });
          } else {
            if (seenTargets.has(target)) {
              warnings.push({
                nodeId: node.id,
                message: `"${label}": ${pos} writes "${target}" again — the later row will overwrite the earlier one.`,
              });
            }
            seenTargets.add(target);
          }

          const source = item.source === "variable" ? "variable" : "manual";
          if (source === "variable") {
            const src = (item.sourceVariable || "").trim();
            if (!src) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": ${pos} is set to "From variable" but no source is selected.`,
              });
            } else {
              const known = (flowVariables || []).some((v) => v.name === src);
              if (!known) {
                warnings.push({
                  nodeId: node.id,
                  message: `"${label}": ${pos} source variable "${src}" is not declared earlier in the flow.`,
                });
              }
            }
          } else {
            // Manual mode: empty value is allowed (clears the variable to ""),
            // but warn on unknown {{vars}} so the user catches typos early.
            checkUnknownVars(item.value || "", flowVariables, node.id, label, `${pos} value`, warnings);
          }
        });
        break;
      }

      // ─── CODE TRANSFORM (codeMap / legacy jsonMap) ───
      case "codeMap":
      case "jsonMap": {
        const inputVar = (d.inputVariable || "").trim();
        const code = (d.code || "").trim();
        const outputRows = Array.isArray(d.outputVariables) ? d.outputVariables : [];
        const legacyOutput = (d.outputVariable || "").trim();
        const outputNames = outputRows.length > 0
          ? outputRows.map((row) => (row.variableName || "").trim()).filter(Boolean)
          : legacyOutput
            ? [legacyOutput]
            : [];

        if (outputNames.length === 0) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Add at least one output variable.`,
          });
        }
        outputNames.forEach((name, idx) => {
          if (!VALID_SET_VARIABLE_NAME.test(name)) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Output variable #${idx + 1} "${name}" is not a valid name.`,
            });
          }
        });
        if (!code) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": JavaScript code is required (must return a value).`,
          });
        }
        if (inputVar && !(flowVariables || []).some((v) => v.name === inputVar)) {
          warnings.push({
            nodeId: node.id,
            message: `"${label}": Input variable "${inputVar}" is not declared — ensure an earlier step sets it.`,
          });
        }
        break;
      }

      // ─── WEBHOOK ───
      case "webhook": {
        const url = d.webhookUrl || "";
        if (!url.trim()) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Webhook URL is empty.`,
          });
        } else if (!URL_PATTERN.test(url)) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Webhook URL is invalid. Must start with http:// or https://.`,
          });
        }

        const headers = d.headers || "{}";
        if (headers.trim()) {
          try {
            JSON.parse(headers);
          } catch {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Webhook headers is not valid JSON.`,
            });
          }
        }
        break;
      }

      // ─── CONDITION ───
      case "condition": {
        const conditions = d.conditions || [];
        if (conditions.length === 0) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": No conditions defined.`,
          });
        } else {
          conditions.forEach((cond, i) => {
            if (!(cond.variable || "").trim()) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": Condition #${i + 1} has no variable set.`,
              });
            }
            if (!(cond.operator || "").trim()) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": Condition #${i + 1} has no operator set.`,
              });
            }
            if (
              cond.operator &&
              !["is_empty", "is_not_empty"].includes(cond.operator) &&
              !(cond.value ?? "").toString().trim()
            ) {
              warnings.push({
                nodeId: node.id,
                message: `"${label}": Condition #${i + 1} has no comparison value.`,
              });
            }
          });
          const lb = d.logicBetween || [];
          if (conditions.length > 1 && lb.length < conditions.length - 1) {
            warnings.push({
              nodeId: node.id,
              message: `"${label}": AND/OR logic between conditions is incomplete; defaulting to AND.`,
            });
          }
        }
        break;
      }

      // ─── DELAY ───
      case "delay": {
        const duration = d.duration;
        if (duration === undefined || duration === null || duration === "") {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Delay duration is not set.`,
          });
        } else if (typeof duration === "number" && duration <= 0) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Delay duration must be greater than 0.`,
          });
        } else if (isNaN(Number(duration))) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Delay duration must be a valid number.`,
          });
        }

        const unit = d.unit || "";
        if (unit && !VALID_DELAY_UNITS.includes(unit)) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Invalid delay unit "${unit}". Must be one of: ${VALID_DELAY_UNITS.join(", ")}.`,
          });
        }
        break;
      }

      // ─── ASSIGN AGENT ───
      case "assignAgent": {
        const agentIds = d.agentIds;
        const legacyAgentId = d.agentId;
        const hasAgents =
          (Array.isArray(agentIds) && agentIds.length > 0) ||
          (legacyAgentId != null && legacyAgentId !== "" && Number(legacyAgentId) > 0);
        if (!hasAgents) {
          warnings.push({
            nodeId: node.id,
            message: `"${label}": No agents selected. Select at least one agent or the node will skip assignment at runtime.`,
          });
        }
        break;
      }

      // ─── AI ASK QUESTION ───
      case "aiAskQuestion": {
        const question = d.question || "";
        if (!question.trim()) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Question text is empty.`,
          });
        }
        const entities = d.entities || [];
        if (entities.length > 0) {
          entities.forEach((ent, idx) => {
            if (!(ent.variableName && String(ent.variableName).trim())) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": Entity #${idx + 1} has no variable to save to.`,
              });
            }
          });
        }
        break;
      }

      // ─── CAPTURE TEXT (ask question, no AI) ───
      case "captureText": {
        const q = d.question || "";
        if (!q.trim()) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Question text is empty.`,
          });
        }
        checkUnknownVars(q, flowVariables, node.id, label, "question", errors);
        const emptyMsg = d.emptyAnswerMessage || "";
        if (emptyMsg) {
          checkUnknownVars(emptyMsg, flowVariables, node.id, label, "empty reply message", errors);
        }
        const vn = d.variableName != null ? String(d.variableName).trim() : "";
        if (!vn) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Choose a variable name to store the user's reply (e.g. selected).`,
          });
        }
        break;
      }

      // ─── AI INTENT CLASSIFIER ───
      case "aiIntentClassifier": {
        const labels = d.labels || [];
        if (labels.length === 0) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": No intent labels defined.`,
          });
        }
        const hd = d.historyDepth;
        if (hd !== undefined && hd !== null && hd !== "") {
          const hdNum = Number(hd);
          if (isNaN(hdNum) || hdNum < 1 || !Number.isInteger(hdNum)) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": History depth must be a positive integer (got "${hd}").`,
            });
          }
        }
        break;
      }

      // ─── AI ENTITY EXTRACTOR ───
      case "aiEntityExtractor": {
        const entities = d.entities || [];
        if (entities.length === 0) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": No entities defined.`,
          });
        }
        break;
      }

      // ─── AI RESPONSE GENERATOR ───
      case "aiResponseGenerator": {
        const prompt = d.prompt || "";
        if (!prompt.trim()) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": AI response prompt is empty.`,
          });
        }
        break;
      }

      // ─── AI KNOWLEDGE BASE ANSWER ───
      case "aiKnowledgeBaseAnswer": {
        const intents = d.intents || [];
        if (!d.knowledgeBaseNodeId && intents.length === 0) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Attach a Knowledge Base or add at least one intent route.`,
          });
        }
        for (const intent of intents) {
          if (!(intent.value || "").trim()) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": An intent route has an empty label.`,
            });
          }
        }
        if (!d.knowledgeBaseNodeId && intents.length > 0) {
          warnings.push({
            nodeId: node.id,
            message: `"${label}": No Knowledge Base attached — only intent routing will work (no FAQ answers).`,
          });
        }
        if (d.knowledgeBaseNodeId && intents.length === 0) {
          // legacy FAQ-only — still require KB (checked above)
        }
        if (d.sessionMode) {
          const mq = d.maxQuestions;
          if (mq !== undefined && mq !== null && mq !== "") {
            const mqNum = Number(mq);
            if (isNaN(mqNum) || mqNum < 1 || !Number.isInteger(mqNum)) {
              errors.push({
                nodeId: node.id,
                message: `"${label}": Max questions must be a positive integer (got "${mq}").`,
              });
            }
          }
        }
        if (d.answerLanguageMode === "fixed_language" && !(d.answerLanguage || "").trim()) {
          warnings.push({
            nodeId: node.id,
            message: `"${label}": Fixed answer language is selected but no language is specified.`,
          });
        }
        const ppmc = d.prePromptMessagesCount;
        if (ppmc !== undefined && ppmc !== null && ppmc !== "" && ppmc !== 0) {
          const ppmcNum = Number(ppmc);
          if (isNaN(ppmcNum) || ppmcNum < 0 || !Number.isInteger(ppmcNum)) {
            errors.push({
              nodeId: node.id,
              message: `"${label}": Pre-prompt messages count must be a non-negative integer (got "${ppmc}").`,
            });
          }
        }
        break;
      }

      // ─── KNOWLEDGE BASE ───
      case "knowledgeBase": {
        const content = d.content || "";
        const attachments = d.attachments || [];
        if (!content.trim() && attachments.length === 0) {
          errors.push({
            nodeId: node.id,
            message: `"${label}": Knowledge Base has no content and no attachments.`,
          });
        }
        break;
      }

      // ─── PRE-APPROVED TEMPLATE ───
      case "preApprovedTemplate": {
        if (!d.templateId && !d.templateName) {
          errors.push({ nodeId: node.id, message: `"${label}": No template selected.` });
        }
        for (const mapping of d.variableMappings || []) {
          if (!mapping.flowVariable?.trim()) {
            warnings.push({
              nodeId: node.id,
              message: `"${label}": Template variable "${mapping.label || mapping.key}" is not mapped.`,
            });
          } else {
            checkUnknownVars(mapping.flowVariable, flowVariables, node.id, label, mapping.key, errors);
          }
        }
        if (d.headerMediaOverride) {
          checkUnknownVars(d.headerMediaOverride, flowVariables, node.id, label, "header media", errors);
        }
        break;
      }

      // ─── INTERACTIVE BUTTONS ───
      case "interactiveButtons": {
        const ibBody = d.body?.text || "";
        if (!ibBody.trim()) errors.push({ nodeId: node.id, message: `"${label}": Body text is empty.` });
        if (ibBody.length > MAX_BODY_LENGTH) errors.push({ nodeId: node.id, message: `"${label}": Body exceeds ${MAX_BODY_LENGTH} characters.` });
        checkUnknownVars(ibBody, flowVariables, node.id, label, "body", errors);
        const ibHeader = d.header?.format || "NONE";
        if (ibHeader === "TEXT" && (d.header?.text || "").length > MAX_HEADER_TEXT_LENGTH) errors.push({ nodeId: node.id, message: `"${label}": Header text exceeds ${MAX_HEADER_TEXT_LENGTH} characters.` });
        if ((d.footer?.text || "").length > MAX_FOOTER_TEXT_LENGTH) errors.push({ nodeId: node.id, message: `"${label}": Footer text exceeds ${MAX_FOOTER_TEXT_LENGTH} characters.` });
        const ibBtns = d.buttons || [];
        if (ibBtns.length > 3) errors.push({ nodeId: node.id, message: `"${label}": Max 3 reply buttons allowed.` });
        for (const btn of ibBtns) {
          if (!(btn.label || "").trim()) errors.push({ nodeId: node.id, message: `"${label}": Button has no label.` });
          if ((btn.label || "").length > 20) errors.push({ nodeId: node.id, message: `"${label}": Button label exceeds 20 characters.` });
          if ((btn.payload || "").length > 256) errors.push({ nodeId: node.id, message: `"${label}": Button payload exceeds 256 characters.` });
        }
        break;
      }

      // ─── INTERACTIVE LIST ───
      case "interactiveList": {
        const ilBody = d.body?.text || "";
        if (!ilBody.trim()) errors.push({ nodeId: node.id, message: `"${label}": Body text is empty.` });
        if (ilBody.length > MAX_BODY_LENGTH) errors.push({ nodeId: node.id, message: `"${label}": Body exceeds ${MAX_BODY_LENGTH} characters.` });
        checkUnknownVars(ilBody, flowVariables, node.id, label, "body", errors);
        if (!d.listFromVariable) {
          const secs = d.listSections || [];
          if (secs.length === 0) warnings.push({ nodeId: node.id, message: `"${label}": No list sections defined.` });
          if (secs.length > 10) errors.push({ nodeId: node.id, message: `"${label}": Max 10 sections allowed.` });
          const totalRows = secs.reduce((sum, s) => sum + (s.rows?.length || 0), 0);
          if (totalRows > 10) errors.push({ nodeId: node.id, message: `"${label}": Max 10 total rows allowed (found ${totalRows}).` });
        }
        break;
      }

      // ─── INTERACTIVE URL ───
      case "interactiveUrl": {
        const iuBody = d.body?.text || "";
        if (!iuBody.trim()) errors.push({ nodeId: node.id, message: `"${label}": Body text is empty.` });
        if (iuBody.length > MAX_BODY_LENGTH) errors.push({ nodeId: node.id, message: `"${label}": Body exceeds ${MAX_BODY_LENGTH} characters.` });
        checkUnknownVars(iuBody, flowVariables, node.id, label, "body", errors);
        const iuUrl = d.url || "";
        if (!iuUrl.trim()) errors.push({ nodeId: node.id, message: `"${label}": URL is empty.` });
        checkUnknownVars(iuUrl, flowVariables, node.id, label, "URL", warnings);
        if ((d.urlLabel || "").length > 20) errors.push({ nodeId: node.id, message: `"${label}": URL label exceeds 20 characters.` });
        break;
      }

      // ─── INTERACTIVE CAROUSEL URL ───
      case "interactiveCarouselUrl": {
        if (!(d.body?.text || "").trim()) errors.push({ nodeId: node.id, message: `"${label}": Carousel message is required.` });
        if (d.cardsSource !== "variable") {
          const cuCards = d.cards || [];
          if (cuCards.length < 2) errors.push({ nodeId: node.id, message: `"${label}": Carousel needs at least 2 cards.` });
          if (cuCards.length > 10) errors.push({ nodeId: node.id, message: `"${label}": Max 10 cards allowed.` });
          cuCards.forEach((card, ci) => {
            if (!["IMAGE","VIDEO"].includes(card.header?.format)) errors.push({ nodeId: node.id, message: `"${label}": Card ${ci + 1} header must be Image or Video.` });
            else if (!(card.header?.mediaUrl || "").trim()) errors.push({ nodeId: node.id, message: `"${label}": Card ${ci + 1} header media URL is required.` });
            if ((card.urlLabel || "").length > 20) errors.push({ nodeId: node.id, message: `"${label}": Card ${ci + 1} URL label exceeds 20 characters.` });
          });
        }
        break;
      }

      // ─── INTERACTIVE CAROUSEL BUTTON ───
      case "interactiveCarouselButton": {
        if (!(d.body?.text || "").trim()) errors.push({ nodeId: node.id, message: `"${label}": Carousel message is required.` });
        if (d.cardsSource !== "variable") {
          const cbCards = d.cards || [];
          if (cbCards.length < 2) errors.push({ nodeId: node.id, message: `"${label}": Carousel needs at least 2 cards.` });
          if (cbCards.length > 10) errors.push({ nodeId: node.id, message: `"${label}": Max 10 cards allowed.` });
          cbCards.forEach((card, ci) => {
            if (!["IMAGE","VIDEO"].includes(card.header?.format)) errors.push({ nodeId: node.id, message: `"${label}": Card ${ci + 1} header must be Image or Video.` });
            else if (!(card.header?.mediaUrl || "").trim()) errors.push({ nodeId: node.id, message: `"${label}": Card ${ci + 1} header media URL is required.` });
            if ((card.buttons || []).length === 0) errors.push({ nodeId: node.id, message: `"${label}": Card ${ci + 1} must have at least 1 button.` });
            if ((card.buttons || []).length > 2) errors.push({ nodeId: node.id, message: `"${label}": Card ${ci + 1} max 2 buttons.` });
            for (const btn of (card.buttons || [])) {
              if ((btn.label || "").length > 20) errors.push({ nodeId: node.id, message: `"${label}": Card button label exceeds 20 characters.` });
              if ((btn.payload || "").length > 256) errors.push({ nodeId: node.id, message: `"${label}": Card button payload exceeds 256 characters.` });
            }
          });
        }
        break;
      }

      // ─── TRANSFER TO FLOW ───
      case "transferToFlow": {
        if (!d.targetFlowId) errors.push({ nodeId: node.id, message: `"${label}": No destination flow selected.` });
        if (d.transferVariableMode === "selected" && !(d.transferVariables || []).filter(Boolean).length) {
          errors.push({ nodeId: node.id, message: `"${label}": Select at least one variable to transfer, or choose "All variables".` });
        }
        break;
      }

      // ─── CONTROLLER ───
      case "controller": {
        const ctrlBranches = d.branches || [];
        if (ctrlBranches.length === 0) errors.push({ nodeId: node.id, message: `"${label}": At least one branch is required.` });
        ctrlBranches.forEach((b, i) => {
          if (!b.variable?.trim()) errors.push({ nodeId: node.id, message: `"${label}": Branch ${i + 1} is missing a variable.` });
        });
        break;
      }

      // ─── END ───
      case "end":
        break;

      default:
        break;
    }
  }

  // ── Edge checks ──
  for (const edge of edges) {
    if (!nodeIds.has(edge.source)) {
      errors.push({
        message: `Edge source "${edge.source}" does not match any node.`,
      });
    }
    if (!nodeIds.has(edge.target)) {
      errors.push({
        message: `Edge target "${edge.target}" does not match any node.`,
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

